import React, { useState, useEffect } from 'react';
import { Truck, Settings, Info } from 'lucide-react';
import { useGetVehicleTypesQuery } from '../../services/api';

interface VehicleType {
  code: string;
  name: string;
  label: string;
  capacity: number;
  weight: number;
  averageSpeed: number;
  eixos: number;
}

interface VehicleConfigurationCardProps {
  selectedVehicleType: string;
  onVehicleTypeChange: (vehicleType: string) => void;
}

export const VehicleConfigurationCard: React.FC<VehicleConfigurationCardProps> = ({
  selectedVehicleType,
  onVehicleTypeChange
}) => {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Buscar tipos de veículos
  const { data: vehicleTypesData, error: typesError } = useGetVehicleTypesQuery();

  useEffect(() => {
    // Normalize response: can be an array or an object with data array
    if (vehicleTypesData) {
      const normalized = Array.isArray(vehicleTypesData)
        ? vehicleTypesData
        : (vehicleTypesData as any)?.data && Array.isArray((vehicleTypesData as any).data)
          ? (vehicleTypesData as any).data
          : [];

      setVehicleTypes(normalized);
      setIsLoading(false);
    } else if (typesError) {
      setError('Erro ao carregar tipos de veículos');
      setIsLoading(false);
    }
  }, [vehicleTypesData, typesError]);

  const handleVehicleTypeChange = (value: string) => {
    onVehicleTypeChange(value);
  };

  return (
    <div 
      className="rounded-xl shadow-lg border p-6"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-primary)'
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Truck className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 
            className="text-xl font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Configuração de Veículos
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'var(--text-secondary)' }}
          >
            Configure o tipo de veículo preferido para cálculo de frete
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <label 
            className="block text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
          >
            Tipo de Veículo para Cálculo de Frete
          </label>
          
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--text-primary)' }}></div>
              <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>Carregando tipos de veículos...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Info className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{error}</span>
            </div>
          ) : vehicleTypes.length === 0 ? (
            <div className="flex items-center gap-2 p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <Info className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                Nenhum tipo de veículo cadastrado para esta empresa
              </span>
            </div>
          ) : (
            <select
              value={selectedVehicleType}
              onChange={(e) => handleVehicleTypeChange(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="">Selecione o tipo de veículo</option>
              {vehicleTypes.map((vehicleType) => (
                <option key={vehicleType.code} value={vehicleType.code}>
                  {vehicleType.label || vehicleType.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedVehicleType && (
          <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4" style={{ color: 'var(--text-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Configuração Atual
              </span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              O tipo de veículo selecionado será usado como padrão para todos os cálculos de frete 
              e planejamento de rotas da empresa.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

